// messagesService.js: REST + WebSocket calls for the DM/messaging feature —
// conversations (DM and group), messages (fetch/send/edit/delete),
// attachment upload, live presence/typing/refresh sockets, and the
// "who can message me" setting.
//
// Messages/DM data layer. This is the ONE seam allowed to know there's no
// real backend yet: everything above it (store/messages/actions.js,
// components) only calls the functions exported here, never mock data
// directly. Swapping to a real backend later means rewriting the bodies of
// these functions (e.g. useAPI() calls + socket.io-client listeners using
// the already-installed socket.io-client dependency) — no store or
// component code needs to change.
import store from '@/store';
import { BaseURL } from '@/global/baseUrls.js';
import { classifyAttachmentType } from '@/helpers/attachmentTypeHelper';
import { CURRENT_USER } from './messagesMockData';

const messageListeners = [];
const typingListeners = [];
const conversationListeners = [];
const presenceListeners = [];

// "Me" for the chat feature should be whoever's actually logged in, not a
// fixed mock identity — matters today (every sent message would otherwise
// claim to be from "Alex Williams" regardless of who's testing) and is
// exactly the shape a real backend integration needs anyway. Falls back to
// the mock identity only when there's no authenticated user (e.g. signed
// out, or exercising the feature without auth set up).
/**
 * Return the current chat identity: derived from the authenticated user
 * when one is logged in, otherwise the mock CURRENT_USER.
 */
export function getCurrentUser() {
  const authUser = store.getters.getUser;
  if (!authUser?.id) return CURRENT_USER;

  const name = `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim() || authUser.username || CURRENT_USER.name;
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || CURRENT_USER.initials;

  return {
    id: authUser.id,
    name,
    initials,
    presence: 'online',
    avatarUrl: authUser.avatarFile?.storageId ? BaseURL + authUser.avatarFile.storageId : '',
  };
}

// Resolves a chat-API auth header the same way fetchUnreadMessagesCount does —
// now that requests go through the same-origin /chat-api proxy, the
// window.fetch patch in main.js DOES attach Authorization automatically —
// this is kept anyway since it's harmless (same token, same value) and this
// module shouldn't depend on that global patch's behavior to function.
function chatApiAuthHeader() {
  const tokenCookie = document.cookie.split('; ').find((c) => c.startsWith('Token='))?.split('=')[1];
  const accessToken = localStorage.getItem('access_token');
  const authToken = accessToken || tokenCookie;
  return authToken ? { Authorization: authToken } : {};
}

async function chatApiRequest(path, { method = 'GET', params = null, body = null } = {}) {
  const query = params ? `?${new URLSearchParams(params)}` : '';
  const response = await fetch(`/chat-api${path}${query}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...chatApiAuthHeader(),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 204) {
    if (!response.ok) throw new Error(`${method} ${path} failed (${response.status})`);
    return null;
  }

  const data = await response.json().catch(() => null);
  // Same convention as the main platform's own FastAPI services (see
  // store/auth/actions.js, store/org/actions.js) — this backend can answer
  // an HTTP 200 that's semantically a failure: a { status_id: 0, reason }
  // body, e.g. rejecting "delete for everyone" past the 15-minute window.
  // Checking response.ok alone let these through as silent successes,
  // leaving whatever optimistic UI change the caller made never reverted.
  if (!response.ok || data?.status_id === 0) {
    throw new Error(data?.reason || `${method} ${path} failed (${response.status})`);
  }
  return data;
}

// Text is intentionally plain (no emoji) — the conversation list renders a
// theme-aware SVG icon (see ConversationList.vue's PREVIEW_ICON_MAP) next to
// this label instead, since emoji glyphs render with their own fixed colors
// on every platform and can't be recolored for dark theme.
const ATTACHMENT_PREVIEW_LABELS = {
  image: 'Photo',
  video: 'Video',
  audio: 'Audio',
  zip: 'Zip',
  file: 'File',
};

// Normalizes a sender reference into a plain user id — the field may already
// be a bare id string, or a populated user sub-document ({ _id }/{ id }) with
// the id key named differently depending on which endpoint supplied it.
function userIdFromValue(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value._id || value.id || value.user_id || value.userId || null;
}

// last_message doesn't reliably carry a sender field at all (see
// resolveLastMessageSenderId's own comment) — when it does, the key name
// varies by endpoint/shape, so every known variant is tried before falling
// back to guessing from locally cached messages.
function lastMessageAuthorId(lastMessage) {
  return userIdFromValue(
    lastMessage?.author ||
      lastMessage?.sender ||
      lastMessage?.sender_id ||
      lastMessage?.senderId ||
      lastMessage?.user_id ||
      lastMessage?.userId
  );
}

// Local-cache mirror of conversationPreviewText (store/messages/actions.js)
// — used only by resolveLastMessageSenderId's fuzzy fallback below, to find
// a cached message whose rendered preview matches last_message's when there's
// no id to match on directly.
function cachedConversationPreviewText(message) {
  if (message.isDeleted) {
    return message.senderId === getCurrentUser().id ? 'You deleted this message' : 'This message was deleted';
  }
  if (message.text) return message.text;
  const attachment = message.attachments?.[0];
  if (attachment) return ATTACHMENT_PREVIEW_LABELS[attachment.type] || 'File';
  if (message.linkPreview) return message.linkPreview.url;
  return '';
}

// Resolves who sent a conversation's last_message, trying progressively
// less certain sources in order:
// 1. A sender id embedded directly on last_message, if this particular
//    response happens to include one (lastMessageAuthorId) — not guaranteed
//    on every shape this endpoint returns.
// 2. An exact id match against this conversation's locally cached messages
//    (messagesByConversation) — populated once the conversation has actually
//    been opened, or right after this client itself sends into it.
// 3. A fuzzy fallback: the most recent cached message whose rendered preview
//    text matches last_message's, within 60s of its timestamp — covers a
//    last_message id that doesn't line up with what's cached (e.g. an
//    optimistic-vs-confirmed id mismatch on a message this client just sent).
// Returns null when none of these resolve — callers (conversationPreviewFromLastMessage)
// treat that as "unknown sender", which is what leaves a group list row's
// "Sender: message" prefix blank rather than guessing wrong.
function resolveLastMessageSenderId(lastMessage, conversationId, previewText) {
  const authorId = lastMessageAuthorId(lastMessage);
  if (authorId) return authorId;

  const cachedMessages = store.state.messages.messagesByConversation[conversationId] || [];
  const lastMessageId = lastMessage?._id || lastMessage?.id;
  const exactCachedMessage = cachedMessages.find((m) => m.id === lastMessageId);
  if (exactCachedMessage?.senderId) return exactCachedMessage.senderId;

  const lastMessageAt = new Date(
    lastMessage?.created_date || lastMessage?.createdAt || lastMessage?.created_at || lastMessage?.timestamp || 0
  ).getTime();
  const hasComparableTime = Number.isFinite(lastMessageAt) && lastMessageAt > 0;
  const fallbackCachedMessage = [...cachedMessages]
    .reverse()
    .find((message) => {
      if (cachedConversationPreviewText(message) !== previewText) return false;
      if (!hasComparableTime) return true;
      return Math.abs((message.createdAt || 0) - lastMessageAt) <= 60000;
    });

  return fallbackCachedMessage?.senderId || null;
}

// last_message on the conversations-list endpoint may carry the same raw
// uploaded-file record as messages/list's `attachments` (see
// mapMessageAttachment above) — either as a single object or, on this
// endpoint, possibly nested in an array — or it may only flag image/video
// via a `type` field with no attachment sub-document at all. Handle both so
// the preview degrades to text-only ("Photo") instead of going blank when
// a thumbnail isn't available.
function conversationPreviewFromLastMessage(lastMessage, conversationId) {
  if (!lastMessage) return { text: '', thumbnailUrl: '', isLink: false, icon: null, senderId: null };

  // "Delete for everyone" clears out text/attachments and sets `is_deleted`
  // (see mapMessageResult's `isDeleted`) — without this check the preview
  // falls through to the empty-text branch below and renders blank instead
  // of the same placeholder MessageBubble shows in the thread itself. Same
  // "You deleted this message" vs "This message was deleted" split
  // MessageBubble.vue's own bubble placeholder uses.
  if (lastMessage.is_deleted) {
    const senderId = resolveLastMessageSenderId(lastMessage, conversationId, '');
    const deletedByMe = senderId === getCurrentUser().id;
    return {
      text: deletedByMe ? 'You deleted this message' : 'This message was deleted',
      thumbnailUrl: '',
      isLink: false,
      icon: 'deleted',
      senderId,
    };
  }

  const attachment = Array.isArray(lastMessage.attachments)
    ? lastMessage.attachments[0]
    : lastMessage.attachments;

  if (attachment?.storageId) {
    const attachmentType = classifyAttachmentType(attachment.mimetype);
    const text = ATTACHMENT_PREVIEW_LABELS[attachmentType];
    return {
      text,
      thumbnailUrl: attachmentType === 'image' ? BaseURL + attachment.storageId : '',
      isLink: false,
      icon: attachmentType,
      senderId: resolveLastMessageSenderId(lastMessage, conversationId, text),
    };
  }

  if (ATTACHMENT_PREVIEW_LABELS[lastMessage.type]) {
    const text = ATTACHMENT_PREVIEW_LABELS[lastMessage.type];
    return {
      text,
      thumbnailUrl: '',
      isLink: false,
      icon: lastMessage.type,
      senderId: resolveLastMessageSenderId(lastMessage, conversationId, text),
    };
  }

  // The backend rejects a truly empty `text` on send, so attachment-only
  // messages go out with a single space as a placeholder (see sendMessage) —
  // trim it here too, otherwise an unrecognized attachment shape falls
  // through to a preview that's just an invisible space instead of empty.
  const text = (lastMessage.text || '').trim();
  if (!text && import.meta.env.DEV) {
    console.warn('[conversationPreviewFromLastMessage] empty preview — unrecognized last_message shape:', lastMessage);
  }
  return {
    text,
    thumbnailUrl: '',
    isLink: Boolean(lastMessage.url?.url && !lastMessage.url.isDisabledPreview),
    icon: null,
    senderId: resolveLastMessageSenderId(lastMessage, conversationId, text),
  };
}

// Real API responses carry the other side's online status as an is_online
// boolean, not a presence string — undefined/null (participant sub-doc
// missing the field) maps to null/"unknown" rather than false/"offline" so
// the UI can hide the dot entirely instead of showing a misleading offline
// indicator.
function presenceFromIsOnline(isOnline) {
  if (isOnline === true) return 'online';
  if (isOnline === false) return 'offline';
  return null;
}

function mapConversationResult(result) {
  if (result.conversation_type === 'group') return mapGroupConversationResult(result);

  // The real /api/messages/conversations/list response wraps the other
  // side of the DM in a one-element `participant` ARRAY, not a plain
  // object — and createMessagingChannel's response instead sends a plural
  // `participants` array. Normalize both shapes and pick whichever entry
  // isn't the current user rather than assuming array-ness or object-ness.
  const currentUserId = getCurrentUser().id;
  const participantList = Array.isArray(result.participant)
    ? result.participant
    : result.participant
    ? [result.participant]
    : result.participants || [];
  const p = participantList.find((participant) => participant._id !== currentUserId) || participantList[0] || {};
  if (!participantList.length && import.meta.env.DEV) {
    console.warn('[mapConversationResult] no resolvable participant — unrecognized conversation shape:', result);
  }
  const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.username || 'Unknown';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

  const lastMessagePreview = conversationPreviewFromLastMessage(result.last_message, result._id);

  return {
    id: result._id,
    type: 'dm',
    // Who created the channel (the "author" of the conversation record
    // itself, not of any individual message) — used by MessageList's
    // "You created this chat..." label to credit whoever actually started
    // it instead of always assuming it was "you".
    creatorId: result.author,
    participant: {
      id: p._id,
      name,
      username: p.username,
      initials,
      presence: presenceFromIsOnline(p.is_online),
      avatarUrl: p.avatarFile?.storageId ? BaseURL + p.avatarFile.storageId : '',
      verified: p.verified,
      userBadgeIcon: p.userBadgeIcon,
      isAnyoneCanMessage: Boolean(p.isAnyoneCanMessage),
    },
    lastMessagePreview: lastMessagePreview.text,
    lastMessageThumbnailUrl: lastMessagePreview.thumbnailUrl,
    lastMessageIsLink: lastMessagePreview.isLink,
    lastMessagePreviewIcon: lastMessagePreview.icon,
    // The list's time column reflects the most recent activity, not when the
    // channel itself was created — prefer last_message's own timestamp, then
    // the conversation's last_modified_time (bumped on every new message),
    // and only fall back to created_date for a brand-new channel with no
    // messages yet. createdAt stays pinned to created_date (channel
    // creation) for the separate "You created this chat on X" label.
    lastMessageAt: new Date(
      result.last_message?.created_date || result.last_modified_time || result.created_date
    ).getTime(),
    createdAt: new Date(result.created_date).getTime(),
    unreadCount: result.unread_count || 0,
  };
}

// Confirmed against a real captured GET /api/messages/conversations/list?
// conversation_type=group response: members come back under `participant`
// (singular key, plural array — same key the DM/'user' shape above uses),
// NOT `participants`. Still checked second as a fallback for whatever shape
// createGroupConversation's own create response turns out to use (no
// captured fixture for that one yet). The group's display name/avatar also
// come from the org this channel is linked to (`organization.name`/
// `organization.avatar.storageId`), not a top-level name/avatarFile on the
// conversation itself — `organization` is absent/unconfirmed on the create
// response, so both fall back to a top-level name/avatarFile for that path.
function mapGroupConversationResult(result) {
  const org = result.organization || {};
  // organization.members[] is the only place role ('owner' | 'admin' |
  // 'editor' | 'reader' | 'member') lives — keyed by userId, separate from
  // the participant[] list below.
  const roleByUserId = {};
  (org.members || []).forEach((m) => {
    if (m?.userId) roleByUserId[m.userId] = m.role;
  });
  // Whether the CURRENT user's own record shows up in participant[] with
  // is_removed set — a real captured response confirmed this happens (an
  // admin removed another member mid-testing and that member's own next
  // GET .../conversations/list still carried their record, is_removed: true).
  // is_deleted is a DIFFERENT, per-user flag — it means this person deleted
  // the conversation from their own inbox (DELETE /api/messages/
  // delete_conversation/:id), not that they left or were removed from the
  // group, so it must NOT feed into group-membership checks like this one.
  // useCanMessageParticipant's canMessageParticipant reads this to disable
  // the composer for a group you've been removed from.
  const currentUserId = getCurrentUser().id;
  const isRemovedFromGroup = (result.participant || []).some(
    (p) => (p._id === currentUserId || p.user_id === currentUserId) && p.is_removed
  );

  // A removed member isn't dropped from result.participant[] server-side —
  // their record stays in the array with is_removed: true (and removed_at
  // set) instead of being deleted outright. Filtering here is what actually
  // makes "Remove" stick — without it, the very next conversations refresh
  // brings them right back into the list, looking exactly like the removal
  // silently reverted. is_deleted is deliberately NOT filtered on here — see
  // the isRemovedFromGroup comment above, it means this participant deleted
  // the conversation from their own inbox, not that they're no longer a
  // group member; everyone else should still see them in the member list.
  const activeParticipants = (result.participant || []).filter((p) => !p?.is_removed);
  const participants = activeParticipants.map((p) =>
    // p.role is confirmed present directly on the participant record (e.g.
    // "admin") — the real source of truth. roleByUserId (organization.members)
    // and the creator guess are both just fallbacks for shapes that don't
    // carry it, in that priority order.
    mapChannelParticipant(p, p.role || roleByUserId[p._id] || (p._id === result.author ? 'admin' : undefined))
  );
  // The requesting user is never included in participant[] (same as DMs
  // excluding the other side's own id from `p`), so their own role has to
  // come straight off organization.members instead of being found inside
  // `participants`. A plain group (created via NewGroupModal, no linked
  // organization) has no organization.members at all, though — roleByUserId
  // is empty for every member there, which would otherwise default even the
  // creator to 'member' and permanently disable their own Add/Remove Members
  // buttons. Fall back to crediting the creator (result.author, same field
  // creatorId below reads) as 'owner' before giving up and calling them a
  // plain 'member'.
  const myRole =
    roleByUserId[getCurrentUser().id] || (result.author === getCurrentUser().id ? 'owner' : 'member');
  const lastMessagePreview = conversationPreviewFromLastMessage(result.last_message, result._id);
  // result.avatarFile comes back as an ARRAY (confirmed against a real
  // captured response right after an updateGroupAvatar PATCH — [{ storageId,
  // ... }], not a bare object), so read the first entry rather than
  // .storageId directly off the array itself.
  const groupAvatarFile = Array.isArray(result.avatarFile) ? result.avatarFile[0] : result.avatarFile;
  // org.avatar's populated shape isn't confirmed (only ever observed as null
  // so far) — handle both a bare url string and a { storageId } object.
  const avatarUrl = org.avatar?.storageId
    ? BaseURL + org.avatar.storageId
    : typeof org.avatar === 'string'
    ? org.avatar
    : groupAvatarFile?.storageId
    ? BaseURL + groupAvatarFile.storageId
    : '';
  const createdAtMs = new Date(result.created_date).getTime();
  // Built from the FULL result.participant[] (including removed/deleted
  // records) rather than activeParticipants — a removed member's "was
  // removed" event needs their record even though they're excluded from the
  // live `participants` list above.
  const membershipEvents = [
    ...buildMembershipEvents(result.participant, createdAtMs),
    ...buildActivityLogEvents(result.activity_logs),
  ];

  // Only resolvable once the sender's message is cached locally — see
  // conversationPreviewFromLastMessage's senderId comment. A fresh app load
  // with nothing cached yet leaves this blank, so the list just shows the
  // message text with no "Name: " prefix rather than a wrong guess. Also
  // left blank when there's no preview text to show it next to — e.g. a
  // last_message with text: null and no attachment/type at all (confirmed
  // against a real captured response) resolves a real senderId but an empty
  // preview, which would otherwise render as a bare "Name:" with nothing
  // after it.
  let lastMessageSenderName = '';
  if (lastMessagePreview.senderId && lastMessagePreview.text) {
    lastMessageSenderName =
      lastMessagePreview.senderId === getCurrentUser().id
        ? 'You'
        : participants.find((p) => p.id === lastMessagePreview.senderId)?.name || '';
  }

  return {
    id: result._id,
    type: 'group',
    creatorId: result.author,
    // org.vue's goToGroupMessaging matches an already-open channel by this
    // field (getGroupConversations.find(c => c.organizationId === ...)) —
    // without it, a re-visit can never find its existing channel and always
    // falls through to (re-)creating one.
    organizationId: org._id || null,
    name: org.name || result.name || 'Group',
    avatarUrl,
    participants,
    memberCount: participants.length,
    // The requesting user's own org role — see roleByUserId comment above
    // for why this can't be read off `participants`.
    myRole,
    lastMessagePreview: lastMessagePreview.text,
    lastMessageSenderName,
    lastMessageThumbnailUrl: lastMessagePreview.thumbnailUrl,
    lastMessageIsLink: lastMessagePreview.isLink,
    lastMessagePreviewIcon: lastMessagePreview.icon,
    lastMessageAt: new Date(
      result.last_message?.created_date || result.last_modified_time || result.created_date
    ).getTime(),
    createdAt: createdAtMs,
    unreadCount: result.unread_count || 0,
    isAnyoneCanMessage: Boolean(result.isAnyoneCanMessage),
    isRemovedFromGroup,
    membershipEvents,
  };
}

// "Who can send messages in this group" — PATCH /api/messaging_channel
// { message_channel_id, isAnyoneCanMessage }. The group's own id (see
// mapGroupConversationResult above) IS the message_channel_id — same
// channel POST /api/messaging_channel created when the group was made.
export async function updateGroupMessagePermission(messageChannelId, isAnyoneCanMessage) {
  return chatApiRequest('/api/messaging_channel', {
    method: 'PATCH',
    body: { message_channel_id: messageChannelId, isAnyoneCanMessage },
  });
}

// Updates a group's avatar. Same PATCH /api/messaging_channel endpoint as
// updateGroupMessagePermission above, just with an avatarFile field
// instead — mirrors that endpoint's shape since there's no separate
// "update group info" endpoint documented; avatarFile is the file id
// returned by POST /api/files (see uploadGroupAvatarImage below).
export async function updateGroupAvatar(messageChannelId, avatarFileId) {
  return chatApiRequest('/api/messaging_channel', {
    method: 'PATCH',
    body: { message_channel_id: messageChannelId, avatarFile: avatarFileId },
  });
}

// Updates a group's name. Same PATCH /api/messaging_channel endpoint as
// updateGroupMessagePermission/updateGroupAvatar above — see
// updateGroupAvatar's own comment for why this shared, undocumented-elsewhere
// endpoint is reused rather than a dedicated one. Unlike avatar/permission
// updates, a real captured request for this one showed the endpoint expects
// isAnyoneCanMessage in the SAME payload as name, not name alone — omitting
// it looked like renaming just silently failed (the PATCH gets rejected and
// the optimistic rename reverts), so the caller must pass the conversation's
// current value through untouched.
export async function updateGroupName(messageChannelId, name, isAnyoneCanMessage) {
  return chatApiRequest('/api/messaging_channel', {
    method: 'PATCH',
    body: { message_channel_id: messageChannelId, name, isAnyoneCanMessage },
  });
}

// Uploads a new group avatar image to the main app's file storage (not
// chat-api) — same POST /api/files, multipart, field name "file" pattern
// proven elsewhere for org avatars. Returns the raw uploaded-file record;
// callers read `_id`/`id`.
export async function uploadGroupAvatarImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch('/api/files', { method: 'POST', body: formData });
  if (!response.ok) throw new Error(`POST /api/files failed (${response.status})`);
  return response.json();
}

// Removes the current user from a group (see exitGroup). Empty payload —
// the URL's messageChannelId is all the endpoint's spec calls for.
export async function leaveGroup(messageChannelId) {
  return chatApiRequest(`/api/messaging_channel/${messageChannelId}/leave-group`, {
    method: 'POST',
  });
}

// Adds one participant to an existing group. Called once per newly-selected
// person (see addGroupMembers) since the endpoint only takes a single
// user_id/role pair, not a batch. role defaults to 'member' — matches the
// local optimistic participant record addGroupMembers builds; only a
// group's creator (see createGroupConversation) starts as 'admin'.
export async function addGroupParticipant(messageChannelId, userId, role = 'member') {
  return chatApiRequest(`/api/messaging_channel/${messageChannelId}/add-participant`, {
    method: 'POST',
    body: { user_id: userId, role },
  });
}

// Removes one participant from an existing group (see removeGroupMember).
export async function removeGroupParticipant(messageChannelId, userId) {
  return chatApiRequest(`/api/messaging_channel/${messageChannelId}/remove-participant`, {
    method: 'POST',
    body: { user_id: userId },
  });
}

// Promotes/demotes one participant (see makeGroupMemberAdmin/
// makeGroupMemberRegular, GroupMembersModal's per-member "Make Group Admin"/
// "Make Group Member" menu items). Only 'admin' and 'member' are ever sent
// from the UI — this app just distinguishes those two for now, no separate
// 'owner' tier the way an org-backed group's organization.members can carry.
export async function updateParticipantRole(messageChannelId, userId, role) {
  return chatApiRequest(`/api/messaging_channel/${messageChannelId}/update-participant-role`, {
    method: 'POST',
    body: { user_id: userId, role },
  });
}

// conversationType filters the list to just 'user' (DMs) or 'group' — the
// endpoint returns one homogeneous type per call, not a mixed list, so
// fetchConversations/fetchMoreConversations in store/messages/actions.js
// call this once per type and merge the results client-side.
/**
 * Fetch a page of conversations from the chat API, mapped into the UI
 * conversation shape, filtered by type ('user'/'group') and optional search.
 */
export async function fetchConversations({ limit = 20, skip = 0, search = '', conversationType = null } = {}) {
  const params = { limit: String(limit), skip: String(skip) };
  if (search) params.search = search;
  if (conversationType) params.conversation_type = conversationType;

  const data = await chatApiRequest('/api/messages/conversations/list', { params });
  const results = Array.isArray(data) ? data : data?.results || [];
  const conversations = results.map(mapConversationResult);

  // The response carries no total count — infer there's another page only
  // when this one came back full.
  return { conversations, hasMore: results.length === limit };
}

function mapPersonResult(user) {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.name || 'Unknown';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

  return {
    id: user.id || user._id,
    name,
    username: user.username,
    initials,
    presence: presenceFromIsOnline(user.is_online),
    avatarUrl: user.avatarFile?.storageId ? BaseURL + user.avatarFile.storageId : '',
    verified: user.verified,
    userBadgeIcon: user.userBadgeIcon,
    // null until this person has an existing DM channel — startConversationWithPerson
    // uses this to decide whether to open it directly or create one first.
    messagingChannelId: user.messaging_channel_id || null,
  };
}

// Backs "Search People" — GET /api/users/list-followers, the main
// /api/ backend (not VITE_CHAT_API like the rest of this module), so it
// rides the same auto-Authorization fetch patch as userConnectionsApi.js.
/**
 * Search followers via GET /api/users/list-followers and map the results
 * into the person shape used by the "Search People" / new-chat panel.
 */
export async function searchPeople({ limit = 20, offset = 0, search = '' } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set('search', search);

  const response = await fetch(`/api/users/list-followers?${params}`);
  if (!response.ok) throw new Error(`GET /api/users/list-followers failed (${response.status})`);
  const data = await response.json();

  const results = Array.isArray(data) ? data : data?.results || [];
  const people = results.map(mapPersonResult);

  // The endpoint returns a total count, unlike /api/messages/conversations/list.
  const count = typeof data?.count === 'number' ? data.count : null;
  return { people, hasMore: count !== null ? offset + results.length < count : results.length === limit };
}

// Backs "Add Members" (new group / add-to-existing-group) — GET /api/users,
// listing all platform users rather than just followers. Same main /api/
// backend as searchPeople above.
/**
 * List/search all users via GET /api/users and map the results into the
 * person shape used by the group member pickers.
 */
export async function listAllUsers({ limit = 20, offset = 0, search = '' } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set('search', search);

  const response = await fetch(`/api/users?${params}`);
  if (!response.ok) throw new Error(`GET /api/users failed (${response.status})`);
  const data = await response.json();

  const results = Array.isArray(data) ? data : data?.results || [];
  const people = results.map(mapPersonResult);

  const count = typeof data?.count === 'number' ? data.count : null;
  return { people, hasMore: count !== null ? offset + results.length < count : results.length === limit };
}

// Whether a conversation participant accepts DMs from anyone —
// /api/messages/conversations/list's participant sub-document (mapConversationResult
// above) never carries isAnyoneCanMessage, only the fuller GET
// /api/users/username/:username profile response does (same field
// profile.vue reads via store/main's usernameData). Fetched directly here,
// same /api/ base as searchPeople above, rather than routed through the
// `main` store module — that module's usernameData is a single shared slot
// several unrelated views (profile, header, post, org, channel) also fetch
// into, and would race with/clobber this lookup.
/**
 * Look up whether a given username accepts DMs from anyone, via
 * GET /api/users/username/:username. Returns false on any missing username
 * or failed request.
 */
export async function fetchIsAnyoneCanMessage(username) {
  if (!username) return false;
  const response = await fetch(`/api/users/username/${username}`);
  if (!response.ok) return false;
  const data = await response.json();
  return Boolean(data?.isAnyoneCanMessage);
}

function mapChannelParticipant(p, role) {
  p = p || {};
  const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.username || 'Unknown';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

  return {
    id: p._id,
    name,
    username: p.username,
    initials,
    presence: presenceFromIsOnline(p.is_online),
    avatarUrl: p.avatarFile?.storageId ? BaseURL + p.avatarFile.storageId : '',
    verified: p.verified,
    userBadgeIcon: p.userBadgeIcon,
    isAnyoneCanMessage: Boolean(p.isAnyoneCanMessage),
    // Org role backing this group — 'owner' | 'admin' | 'editor' | 'reader' |
    // 'member'. Only meaningful for group participants; DMs don't pass this.
    role: role || 'member',
    // A real captured .../conversations/list response has some participant
    // records keyed joined_at/removed_at (snake_case) and others joinedAt/
    // removedAt (camelCase) — read both so buildMembershipEvents below
    // doesn't silently drop entries typed the "wrong" way. is_deleted/
    // deleted_at is a SEPARATE, per-user "deleted this conversation from my
    // own inbox" flag — unrelated to group membership, so it's deliberately
    // not read here.
    joinedAt: p.joined_at || p.joinedAt || null,
    isRemoved: Boolean(p.is_removed),
    removedAt: p.removed_at || p.removedAt || null,
  };
}

// added_by/removed_by come back as a partial user record (id/username/
// firstName/lastName — no avatarFile in any captured response so far), not
// the fuller shape mapChannelParticipant expects — just enough to name the
// actor for buildMembershipEvents below.
function mapMembershipActor(raw) {
  if (!raw?._id) return null;
  const name = `${raw.firstName || ''} ${raw.lastName || ''}`.trim() || raw.username || 'Someone';
  return { id: raw._id, name };
}

// Builds "X added Y to the group" / "X removed Y from the group" / "Y left
// the group" timeline entries from every participant record the API
// returned (including removed ones, which mapGroupConversationResult's
// `participants` deliberately filters out — see activeParticipants above).
// added_by/removed_by are only sometimes populated by the backend — when
// null, these fall back to the actor-less phrasing instead of guessing who
// did it. A removed record with no removed_by (or removed_by pointing at
// the removed member themself) is treated as a voluntary exit ("left the
// group") rather than an admin removal — there's no separate flag for
// "this member left on their own" distinct from is_removed itself.
// joined_at/removed_at have been observed keyed both snake_case and
// camelCase (see mapChannelParticipant above). Confirmed from a real captured
// participant record: they also come back as a NAIVE datetime string with no
// timezone designator at all — e.g. "2026-09-08T10:09:34.014000" — unlike
// created_date, which always carries a trailing "Z" (UTC). Per the Date
// parsing spec, a date-TIME string with no timezone designator is parsed as
// LOCAL time, not UTC; in IST (UTC+5:30) that silently shifts a removal back
// by 5.5 hours relative to a message's real UTC instant, sorting "removed"
// events hours before messages sent well after the removal actually
// happened. Both fields come off the same backend clock as created_date, so
// a designator-less string is treated as UTC (append "Z") rather than left
// to the runtime's local-timezone default.
function toEpochMs(value) {
  if (!value) return null;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  const str = String(value).trim();
  if (!str) return null;
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(str);
  const parsed = new Date(hasTimezone ? str : `${str}Z`).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

const ORIGINAL_MEMBER_WINDOW_MS = 5000;
function buildMembershipEvents(rawParticipants, groupCreatedAtMs) {
  const events = [];
  (rawParticipants || []).forEach((p) => {
    const participant = mapChannelParticipant(p);
    const addedBy = mapMembershipActor(p.added_by);
    const removedBy = mapMembershipActor(p.removed_by);

    if (participant.joinedAt) {
      const joinedAtMs = toEpochMs(participant.joinedAt);
      if (joinedAtMs !== null && joinedAtMs - groupCreatedAtMs > ORIGINAL_MEMBER_WINDOW_MS) {
        events.push({ type: 'joined', at: joinedAtMs, participant, actor: addedBy });
      }
    }
    if (participant.isRemoved && participant.removedAt) {
      const removedAtMs = toEpochMs(participant.removedAt);
      if (removedAtMs === null) return;
      const isSelfExit = !removedBy || removedBy.id === participant.id;
      events.push({
        type: isSelfExit ? 'left' : 'removed',
        at: removedAtMs,
        participant,
        actor: isSelfExit ? null : removedBy,
      });
    }
  });
  return events;
}

// "X changed the Group Name to Y" / "X changed the Group Icon" / "X changed
// the role of Y to Admin/Member" timeline entries — unlike buildMembershipEvents
// above (client reconstructs the label from raw participant fields), the
// backend already renders these as plain text in result.activity_logs (a
// real captured response confirmed the shape: { action: 'edited',
// created_by, log_time, text }), so there's no participant/actor to resolve
// here, just the ready-made text and when it happened.
function buildActivityLogEvents(activityLogs) {
  return (activityLogs || [])
    .map((log) => {
      const at = toEpochMs(log.log_time);
      if (at === null || !log.text) return null;
      return { type: 'activity', at, text: log.text };
    })
    .filter(Boolean);
}

// Creates a DM channel for a person whose search result came back with no
// messaging_channel_id — after this, that channel's `id` is what everything
// else in this module calls message_channel_id.
/** Create a new 1:1 DM channel for the given participants via POST /api/messaging_channel. */
export async function createMessagingChannel(participantIds) {
  const result = await chatApiRequest('/api/messaging_channel', {
    method: 'POST',
    body: { conversation_type: 'user', participants: participantIds },
  });

  const currentUserId = getCurrentUser().id;
  const other = (result.participants || []).find((p) => p._id !== currentUserId) || result.participants?.[0] || {};

  return {
    id: result.id,
    type: 'dm',
    // Whoever calls createMessagingChannel is, by definition, the one
    // starting this chat for the first time.
    creatorId: currentUserId,
    participant: mapChannelParticipant(other),
    lastMessagePreview: '',
    lastMessageAt: new Date(result.created_date).getTime(),
    createdAt: new Date(result.created_date).getTime(),
    unreadCount: 0,
  };
}

// Request/response shape for group creation — POST /api/messaging_channel/
// create_group_conversations, a dedicated group-creation endpoint (unlike
// createMessagingChannel's generic /api/messaging_channel) taking a name and
// N participants, each carrying its own role. The caller is responsible for
// including the current user in participantIds, as its first entry (see
// createAndOpenGroup in store/messages/actions.js) — that entry becomes the
// group's 'admin'; every other participant starts as a plain 'member' (same
// default addGroupParticipant uses for members added later). organizationId
// is optional — only set when this group backs an organization's Group Chat
// (see openGroupChatForOrg), so the backend can link the channel back to it.
/**
 * Create a new group conversation via POST /api/messaging_channel/create_group_conversations,
 * optionally linking it to an organization's Group Chat.
 */
export async function createGroupConversation({ name, participantIds, organizationId = null }) {
  // Members missing an id (e.g. an org member record with no user id
  // resolved yet) must not reach the backend as a literal null entry.
  const ids = (participantIds || []).filter(Boolean);
  const requestParticipants = ids.map((userId, index) => ({ user_id: userId, role: index === 0 ? 'admin' : 'member' }));
  const body = { name, participants: requestParticipants };
  if (organizationId) body.organization_id = organizationId;

  const result = await chatApiRequest('/api/messaging_channel/create_group_conversations', {
    method: 'POST',
    body,
  });

  const currentUserId = getCurrentUser().id;
  const participants = (result.participants || []).map(mapChannelParticipant);

  return {
    id: result.id,
    type: 'group',
    creatorId: currentUserId,
    name: result.name || name,
    avatarUrl: result.avatarFile?.storageId ? BaseURL + result.avatarFile.storageId : '',
    participants,
    memberCount: participants.length,
    // Whoever calls createGroupConversation is the group's creator, i.e. its
    // owner — this response has no organization.members list to look role up
    // from (see mapGroupConversationResult), so it's inferred here instead.
    myRole: 'owner',
    lastMessagePreview: '',
    lastMessageThumbnailUrl: '',
    lastMessageIsLink: false,
    lastMessagePreviewIcon: null,
    lastMessageAt: new Date(result.created_date).getTime(),
    createdAt: new Date(result.created_date).getTime(),
    unreadCount: 0,
  };
}

// Org-backed groups (openGroupChatForOrg in store/messages/actions.js, the
// "Group Messaging" button on org.vue) create their channel via the GENERIC
// POST /api/messaging_channel endpoint — same one createMessagingChannel
// above uses for a DM, just conversation_type: 'group' plus organization_id
// instead of 'user' — rather than the dedicated .../create_group_conversations
// endpoint createGroupConversation above uses for a manually-created
// ("New Group") chat. Routing an org's group through create_group_conversations
// instead returns a 400: the backend only accepts an organization_id-linked
// group through this generic endpoint.
export async function createOrgGroupConversation({ name, participantIds, organizationId }) {
  // Members missing an id (e.g. an org member record with no user id
  // resolved yet) must not reach the backend as a literal null entry. Sent
  // as a bare array of ids — unlike createGroupConversation's dedicated
  // endpoint, this generic one rejects { user_id, role } participant
  // objects, so plain ids are what it expects here.
  const ids = (participantIds || []).filter(Boolean);
  const body = { conversation_type: 'group', name, participants: ids };
  if (organizationId) body.organization_id = organizationId;

  const result = await chatApiRequest('/api/messaging_channel', {
    method: 'POST',
    body,
  });

  const currentUserId = getCurrentUser().id;
  const participants = (result.participants || []).map(mapChannelParticipant);

  return {
    id: result.id,
    type: 'group',
    creatorId: currentUserId,
    // Set locally so a re-visit within the same session (org.vue's
    // goToGroupMessaging) can find this conversation via
    // getGroupConversations.find(c => c.organizationId === ...) immediately,
    // without waiting on the next GET .../conversations/list refetch.
    organizationId: organizationId || null,
    name: result.name || name,
    avatarUrl: result.avatarFile?.storageId ? BaseURL + result.avatarFile.storageId : '',
    participants,
    memberCount: participants.length,
    // Whoever calls this is the group's creator, i.e. its owner — this
    // response has no organization.members list to look role up from (see
    // mapGroupConversationResult), so it's inferred here instead.
    myRole: 'owner',
    lastMessagePreview: '',
    lastMessageThumbnailUrl: '',
    lastMessageIsLink: false,
    lastMessagePreviewIcon: null,
    lastMessageAt: new Date(result.created_date).getTime(),
    createdAt: new Date(result.created_date).getTime(),
    unreadCount: 0,
  };
}

// The backend echoes the attachment back as the full uploaded-file record
// (from POST /api/files) — { _id, storageId, filename, mimetype, size, ... }
// — not the { id, type, url, name } shape MessageBubble renders. Same S3
// URL pattern already used for avatars in this file (BaseURL + storageId).
//
// GET /api/messages/list returns this fully populated, but POST
// /api/messages' own create response has been observed coming back with an
// incomplete attachments sub-document (missing filename) — `fallback` is the
// locally-known upload result (see sendMessage below), used to fill in
// whatever the server's response is missing rather than showing a blank name.
function mapMessageAttachment(attachment, fallback) {
  const mimeType = attachment?.mimetype;
  return {
    id: attachment?._id || fallback?.id,
    type: mimeType ? classifyAttachmentType(mimeType) : fallback?.type || 'file',
    url: attachment?.storageId ? BaseURL + attachment.storageId : fallback?.url || '',
    name: attachment?.filename || fallback?.name || '',
    size: attachment?.size ?? fallback?.size,
  };
}

// Best-guess field name/shape: `reply_message`, nested `{ _id, type, text,
// attachments }` — NOT actually confirmed against a real captured response
// (no fixture/doc for this exists in the repo), so this is still guessing.
// This echoed reference has no `author`/sender field at all, unlike a full
// MessageItem, so `senderId` below can't come from the wire and is recovered
// best-effort from the original message if it's still loaded in this
// conversation's history (e.g. within the current pagination window).
// `attachments` on this nested shape is a *list*, unlike the singular
// object the top-level message uses (see mapMessageAttachment above) — take
// the first entry. Falls back to `result.reply_message_id` (a top-level
// field) since that's the exact field name sendMessage sends the original
// message's id under — the backend may well echo it back the same way
// rather than nesting it inside `reply_message._id`/`.id`.
function mapReplyTo(result, conversationId) {
  const reply = result.reply_message;
  if (!reply) return null;

  if (import.meta.env.DEV) {
    console.debug('[mapReplyTo] raw reply_message from API:', reply, '| result.reply_message_id:', result.reply_message_id);
  }

  const replyId = reply._id || reply.id || result.reply_message_id;
  const original = store.state.messages.messagesByConversation[conversationId]?.find((m) => m.id === replyId);
  const attachment = Array.isArray(reply.attachments) ? reply.attachments[0] : reply.attachments;
  return {
    id: replyId,
    text: (reply.text || '').trim(),
    senderId: original?.senderId,
    attachments: attachment ? [mapMessageAttachment(attachment)] : [],
    linkPreview: null,
  };
}

function mapMessageResult(result, conversationId, localAttachment) {
  return {
    id: result._id,
    conversationId,
    senderId: result.author,
    // The backend rejects a truly empty `text` ("Mandatory Key Text
    // Missing"), so attachment-only sends go out with a single space (see
    // sendMessage below) — trim it back out here so it doesn't render as a
    // stray blank line in the bubble.
    text: (result.text || '').trim(),
    // The backend sends one attachment as a single object, not an array —
    // the UI (MessageBubble) still expects an array, matching the
    // one-image-at-a-time composer's own staged-items shape. Also fall back
    // to the local upload result when the create response's `type` says
    // 'file'/'image'/'video' but doesn't echo an `attachments` sub-document
    // at all — otherwise a known-good local attachment gets discarded.
    replyTo: mapReplyTo(result, conversationId),
    attachments: result.attachments || localAttachment ? [mapMessageAttachment(result.attachments, localAttachment)] : [],
    // The backend now echoes back the `url` object sent in sendMessage —
    // same shape LinkPreview.vue/OGData already expect (url/title/
    // description/imageUrl), plus isDisabledPreview which suppresses the
    // card entirely when set. Every message gets a `url` object regardless
    // of type (`{}` for plain text) — check `url.url` itself, not just the
    // object's presence, or every text message would false-positive as a link.
    linkPreview: result.url?.url && !result.url.isDisabledPreview
      ? {
          url: result.url.url,
          title: result.url.title,
          description: result.url.description,
          imageUrl: result.url.imageUrl,
        }
      : null,
    createdAt: new Date(result.created_date).getTime(),
    status: result.status || 'sent',
    // Server-confirmed edit flag — without this, the "Edited" label (see
    // MessageBubble.vue) only lived as long as the optimistic local state
    // editMessage set it to, and silently disappeared the moment the
    // conversation was refetched (page reload, or re-selecting the thread).
    edited: Boolean(result.is_edited),
    // "Delete for everyone" — the message stays in the list (for every
    // participant, including the deleter) but its content is gone; the
    // bubble renders a "You deleted this message"/"This message was
    // deleted" placeholder instead. A local "delete for me" never sets
    // this — that removes the message from this client's list entirely
    // (see store/messages/actions.js's deleteMessages).
    isDeleted: Boolean(result.is_deleted),
  };
}

/**
 * Fetch a page of messages for a conversation from the chat API, reversed
 * into chronological (oldest-first) order for display.
 */
export async function fetchMessages(conversationId, { limit = 20, skip = 0, search = '' } = {}) {
  const params = { message_channel_id: conversationId, limit: String(limit), skip: String(skip) };
  if (search) params.search = search;

  const data = await chatApiRequest('/api/messages/list', { params });
  const results = Array.isArray(data) ? data : data?.results || [];
  // The API returns newest-first; the UI expects chronological (oldest
  // first, newest at the bottom) order, so just flip the page as-is
  // rather than re-sorting by createdAt.
  const messages = results
    .map((result) => mapMessageResult(result, conversationId))
    .reverse();

  return { messages, hasMore: results.length === limit };
}

// The backend's `type` enum only accepts 'text'/'image'/'video'/'gif'/'file'/
// 'url'/'audio' — 'zip' is a client-only classification (see
// classifyAttachmentType, used to show an archive icon) and gets rejected
// ("Input should be 'text', 'image', ... 'audio'") if sent as-is.
function backendMessageType(attachmentType) {
  return attachmentType === 'zip' ? 'gif' : attachmentType;
}

// Accepts either a plain string (back-compat) or
// { text, attachments, replyMessageId, linkPreview }.
/**
 * Send a text, attachment, reply, or link-preview message to a conversation
 * via POST /api/messages.
 */
export async function sendMessage(conversationId, payload) {
  const { text = '', attachments = [], replyMessageId = null, linkPreview = null } =
    typeof payload === 'string' ? { text: payload } : (payload || {});

  // The backend treats an empty string as a missing key ("Mandatory Key
  // Text Missing") — an attachment-only send (no caption) still needs a
  // non-empty value here; mapMessageResult trims it back out on the way in.
  const body = {
    message_channel_id: conversationId,
    type: attachments.length ? backendMessageType(attachments[0].type) : (linkPreview ? 'url' : 'text'),
    text: text || ' ',
  };
  if (replyMessageId) body.reply_message_id = replyMessageId;
  if (attachments.length) body.attachments = attachments[0].id;
  if (linkPreview) {
    body.url = {
      url: linkPreview.url,
      title: linkPreview.title || '',
      description: linkPreview.description || '',
      imageUrl: linkPreview.imageUrl || '',
      isDisabledPreview: false,
    };
  }

  const result = await chatApiRequest('/api/messages', { method: 'POST', body });
  return mapMessageResult(result, conversationId, attachments[0]);
}

/** Mark a conversation as read via POST /api/messages/mark-as-read. */
export async function markConversationRead(conversationId) {
  return chatApiRequest('/api/messages/mark-as-read', {
    method: 'POST',
    body: { message_channel_id: conversationId },
  });
}

// Confirmed payload shape: no message_channel_id, and the flag is
// delete_for_me (true for "delete for me", false for "delete for
// everyone") rather than a delete_for_everyone flag.
/** Delete one or more messages, either just for the current user or for everyone, via DELETE /api/messages. */
export async function deleteMessages(conversationId, messageIds, forEveryone = false) {
  return chatApiRequest('/api/messages', {
    method: 'DELETE',
    body: { message_ids: messageIds, delete_for_me: !forEveryone },
  });
}

// Deletes an entire conversation (the chat-list row itself), not just its
// messages — distinct from the local-only clearConversationMessages, which
// empties a conversation but keeps its row.
/** Delete an entire conversation (chat-list row and its messages) via DELETE /api/messages/delete_conversation/:id. */
export async function deleteConversation(conversationId) {
  return chatApiRequest(`/api/messages/delete_conversation/${conversationId}`, { method: 'DELETE' });
}

/** Edit a message's text via PATCH /api/messages/:messageId. */
export async function editMessage(conversationId, messageId, text) {
  return chatApiRequest(`/api/messages/${messageId}`, {
    method: 'PATCH',
    body: { text },
  });
}

// No download-tracking endpoint exists yet — the actual file save
// (MessageBubble.vue's requestDownload) already works today via the
// existing downloadFile() utility against the attachment's own URL, so
// this is purely a placeholder seam for whatever the backend eventually
// wants recorded (download counts, audit log, etc.). Swap the body for a
// real call once that endpoint is confirmed; callers only ever await it.
/** Placeholder for logging an attachment download — no backend endpoint exists yet, so this always resolves without recording anything. */
export async function logAttachmentDownload(conversationId, messageId) {
  return Promise.resolve({ conversationId, messageId, logged: false });
}

// Real upload — same endpoint the post composer uses (POST /api/files,
// multipart, field name "file"). The response is the full uploaded-file
// record (same shape mapMessageAttachment reads on the way back in) —
// `{ _id, storageId, filename, mimetype, size, ... }`, not `{ id }`. This is
// the main app's /api/ proxy (VITE_BASE_URL), not the chat backend — so
// it's a relative path and rides the same auto-Authorization window.fetch
// patch as searchPeople above, no manual auth header needed.
/** Upload a file via POST /api/files (multipart) and return a local attachment descriptor with an object URL preview. */
export async function uploadAttachment(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch('/api/files', { method: 'POST', body: formData });
  if (!response.ok) throw new Error(`POST /api/files failed (${response.status})`);
  const result = await response.json();

  return {
    id: result._id,
    type: classifyAttachmentType(file.type),
    url: URL.createObjectURL(file),
    name: result.filename || file.name,
    size: file.size,
  };
}

// --- Live chat WebSocket transport --------------------------------------
// Raw WebSocket (not Socket.io) endpoints on the same chat backend host as
// the REST calls above — same-origin /chat-api proxy (vite.config.js in
// dev, Docker/nginx/default.conf in prod) handles both REST and these WS
// upgrades, so no separate host/env var is needed. No payload-shape spec
// was given by the backend team for any of these three connections —
// parseWsFrame below is deliberately defensive (tries common wrapper keys)
// so a mismatch is a one-place fix once real frames are observed.

function buildChatWsUrl(path) {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Confirmed against real captured traffic on this same chat backend
  // (dev-cnchatapis.digicollect.com, one of this app's own VITE_CHAT_API
  // values): the WS handshake wants the token wrapped in literal double
  // quotes, matching the raw `access_token` cookie format the backend's
  // other clients receive it in — NOT the bare value the REST Authorization
  // header uses. Don't "clean up" this quoting; it's the part that's easy
  // to get wrong when touching this function.
  const token = encodeURIComponent(`"${chatApiAuthHeader().Authorization || ''}"`);
  return `${proto}//${window.location.host}/chat-api${path}?token=${token}`;
}

function parseWsFrame(raw) {
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  return data?.data || data?.payload || data?.message || data;
}

const RECONNECT = { initialDelay: 1000, multiplier: 2, maxDelay: 30000 };
// 20s matches the ping cadence confirmed on the reference client for this
// same backend — chosen there to detect a silently-dead connection faster
// than relying on OS-level TCP timeouts.
const HEARTBEAT_INTERVAL_MS = 20000;

// Generic self-reconnecting WebSocket wrapper shared by all 3 connections
// below. Reconnects with exponential backoff + jitter (same
// min(initialDelay * multiplier**attempt, maxDelay) formula App.vue's own
// retry helper uses for unread-count refresh) — unbounded attempts, since a
// live socket must keep retrying for as long as the feature is active,
// unlike App.vue's finite-attempt case. Sends a small app-level heartbeat
// since a browser WebSocket has no protocol-level ping API, and an idle
// connection would otherwise eventually get dropped by an intermediate proxy.
function openManagedSocket(path, onFrame) {
  let socket = null;
  let attempt = 0;
  let heartbeatTimer = null;
  let reconnectTimer = null;
  let closedByCaller = false;

  const clearTimers = () => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    heartbeatTimer = null;
    reconnectTimer = null;
  };

  const scheduleReconnect = () => {
    if (closedByCaller) return;
    const base = Math.min(RECONNECT.initialDelay * (RECONNECT.multiplier ** attempt), RECONNECT.maxDelay);
    const jitter = base * 0.2 * (Math.random() * 2 - 1);
    attempt += 1;
    reconnectTimer = setTimeout(connect, Math.max(0, base + jitter));
  };

  function connect() {
    // Re-checked here (not just in scheduleReconnect) so an intentional
    // close() that races a pending reconnect timer never opens a new socket
    // after the caller has already said it doesn't want this connection.
    if (closedByCaller) return;
    try {
      socket = new WebSocket(buildChatWsUrl(path));
    } catch (error) {
      scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      attempt = 0;
      heartbeatTimer = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) {
          try {
            socket.send(JSON.stringify({ type: 'ping' }));
          } catch {
            // Best-effort — a failed send here just means the connection is
            // already dead, and onclose (below) will handle reconnecting.
          }
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    socket.onmessage = (event) => {
      const payload = parseWsFrame(event.data);
      // ws_channels/ws_conversations treat ANY frame as "something changed,
      // refetch" (see connectRealtime below) — if the backend echoes our own
      // heartbeat ping back on the same connection (common for WS backends
      // that don't separate control traffic from data), that echo would
      // otherwise be misread as a real signal and trigger a REST refetch
      // every HEARTBEAT_INTERVAL_MS regardless of whether anything actually
      // changed. Filter it out here, once, for all three sockets.
      if (payload && payload.type !== 'ping' && payload.type !== 'pong') onFrame(payload);
    };

    // onclose fires right after onerror in browsers — reconnect is handled
    // there so a single drop doesn't schedule two reconnect attempts.
    socket.onclose = () => {
      clearTimers();
      scheduleReconnect();
    };
  }

  connect();

  return {
    close() {
      closedByCaller = true;
      clearTimers();
      if (socket && socket.readyState <= WebSocket.OPEN) {
        try {
          socket.close();
        } catch {
          // Already closing/closed — nothing to do.
        }
      }
    },
  };
}

let activeChannelSocket = null;
let activeMessagesPollTimer = null;
let realtimeStarted = false;

// Presence stopgap up above already had to confirm (via DevTools) that
// ws_presence connects fine but never actually pushes a single frame, even
// during real activity — no backend confirmation was ever given that
// ws_channels' push behavior is any more reliable, and it's the same
// unconfirmed-payload, connects-over-the-same-transport socket. Poll this
// conversation's own messages directly as a backstop so the open thread
// doesn't go stale indefinitely if that turns out to be true here too —
// mirrors the presence poll's exact reasoning, just shorter, since a thread
// the user is actively looking at should catch up faster than an
// online/offline dot.
const ACTIVE_MESSAGES_POLL_INTERVAL_MS = 10000;

// Called once from initMessages (store/messages/actions.js), guarded the
// same way state.hasInitialized guards the rest of that action — so
// presence/conversation sockets only ever connect once the user has opened
// /messages this session, matching the feature's existing lazy-init
// convention rather than connecting app-wide from login.
/**
 * Open the presence/conversations WebSockets and start watching the active
 * conversation to open/close its own message socket. Idempotent — a second
 * call is a no-op.
 */
export function connectRealtime() {
  if (realtimeStarted) return;
  realtimeStarted = true;

  // Keeping this connection open is itself what marks the current user
  // online to the backend. presence/status field names are a best guess
  // (see parseWsFrame's wrapper-key handling above for the same caveat).
  openManagedSocket('/api/messages/ws_presence', (payload) => {
    const userId = payload.user_id || payload.userId || payload._id || payload.id;
    const presence = payload.presence || payload.status || (payload.isOnline || payload.is_online ? 'online' : 'offline');
    if (!userId) return;
    presenceListeners.forEach((cb) => cb({ userId, presence }));
  });

  openManagedSocket('/api/messages/ws_conversations', (payload) => {
    conversationListeners.forEach((cb) => cb(payload));
  });

  // One watcher covers every path that changes activeConversationId
  // (selectConversation, startConversationWithPerson,
  // openGroupChatForOrg/createAndOpenGroup, and Messages/index.vue's
  // onUnmounted, which sets it to null via a raw commit bypassing any
  // action) — so there's a single place managing the per-conversation
  // socket's lifecycle instead of a call site in each of them, and no
  // leaked connection when leaving a conversation or the /messages route.
  store.watch(
    (state) => state.messages.activeConversationId,
    (conversationId) => {
      if (activeChannelSocket) {
        activeChannelSocket.close();
        activeChannelSocket = null;
      }
      if (activeMessagesPollTimer) {
        clearInterval(activeMessagesPollTimer);
        activeMessagesPollTimer = null;
      }
      if (!conversationId) return;
      // Signal-only, same as ws_conversations above — the frame body is
      // never a full, reliable message object (no confirmed payload shape),
      // so treating it as one produced empty bubbles whenever a frame's
      // shape didn't match mapMessageResult's expectations. Just refetch the
      // channel's messages from the known-good REST endpoint instead.
      activeChannelSocket = openManagedSocket(
        `/api/messages/ws_channels/${conversationId}`,
        () => messageListeners.forEach((cb) => cb(conversationId))
      );
      // Backstop for ws_channels not reliably pushing frames — see
      // ACTIVE_MESSAGES_POLL_INTERVAL_MS's own comment above. Dispatches the
      // same action the socket signal does (refreshActiveMessages), just on
      // a fixed interval instead of waiting on a frame that may never come.
      activeMessagesPollTimer = setInterval(() => {
        store.dispatch('refreshActiveMessages', conversationId);
      }, ACTIVE_MESSAGES_POLL_INTERVAL_MS);
    }
  );
}

/** Register a callback invoked with { userId, presence } whenever a participant's presence changes. */
export function onPresenceChange(callback) {
  presenceListeners.push(callback);
}

/** Register a callback invoked whenever the conversations list should be refetched. */
export function onConversationsRefresh(callback) {
  conversationListeners.push(callback);
}

// Fires on any ws_channels frame for the currently open conversation — the
// callback is responsible for refetching that conversation's messages via
// fetchMessages and merging the result (see store/messages/actions.js).
/** Register a callback invoked whenever the currently open conversation should refetch its messages. */
export function onMessagesRefresh(callback) {
  messageListeners.push(callback);
}

/** Register a callback for typing-status notifications (currently never invoked — see setTypingStatus). */
export function onTyping(callback) {
  typingListeners.push(callback);
}

/** No-op placeholder for broadcasting typing status — kept so callers don't need to change once a real transport exists. */
export function setTypingStatus(_conversationId, _isTyping) {
  // No real backend to forward this to yet — a real transport would emit
  // this over the socket. Kept as a no-op so MessageInput's typing-notify
  // wiring doesn't need to change once a real backend exists.
}

// "Who can DM you" — PATCH /api/users/me { isAnyoneCanMessage }. getMe (see
// store/auth/actions.js) already loads this field onto the current user at
// login, so reading it back needs no separate GET — same pass-through
// getUser getter getCurrentUser() above reads from, just not stripped down
// to its subset.
/** Read the current user's "who can message me" setting from the already-loaded auth profile. */
export async function fetchMessageSettings() {
  const authUser = store.getters.getUser;
  return { allowMessagesFrom: authUser?.isAnyoneCanMessage ? 'everyone' : 'followers' };
}

/** Update the current user's "who can message me" setting via PATCH /api/users/me, and sync the cached auth profile. */
export async function updateMessageSettings(allowMessagesFrom) {
  const response = await fetch('/api/users/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isAnyoneCanMessage: allowMessagesFrom === 'everyone' }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `PATCH /api/users/me failed (${response.status})`);
  }
  // Same convention as editProfileModalNew.vue's PATCH: keep the auth
  // module's cached profile in sync with whatever the backend just saved.
  const updatedUser = await response.json();
  store.commit('setUserData', updatedUser);
  return { allowMessagesFrom };
}
